import React, {useContext, useEffect, useRef, useState} from 'react';
import { Nav } from 'react-bootstrap';
import Scrollspy from 'react-scrollspy';
import styled, {ThemeContext} from 'styled-components';
import { Category } from '../../../../contexts/site/site-context';
import useScrollPositionOnElement from '../../../../hooks/useScrollPositionOnElement';
import NavPill from '../../../general/NavPill';

type MenuCategorySelectorProps = {
    categories?: Category[];
    bannerOffset?: Boolean;
    bannerOffsetHeight?: number;
};

const MenuCategorySelector = ({ categories, bannerOffset, bannerOffsetHeight }: MenuCategorySelectorProps) => {

    const themeContext = useContext(ThemeContext);
    const navHorizontalScroll = useScrollPositionOnElement('.scrollSpyScroll');
    //const [navHorizontalScrollMax, setNavHorizontalScrollMax] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const [scrolling, setScrolling] = useState(false);

    const scrollToActive = () => {

        let el = document.querySelector('.myScroll div.active') as HTMLElement|null;
        let parent = document.querySelector('.scrollSpyScroll') as HTMLElement|null;
        let parentWrapper = document.querySelector('.myScroll') as HTMLElement|null;

        let ourOffset = 30;

        if ( el && parent && parentWrapper && !scrolling) {

            const elLeft = el.offsetLeft + el.offsetWidth;
            const elParentLeft = parent.offsetLeft + parent.offsetWidth;

            // check if element not in view
            if (elLeft >= elParentLeft + parent.scrollLeft) {
                parent.scrollLeft = elLeft - elParentLeft + ourOffset;
            } else if (elLeft <= parent.offsetLeft + parent.scrollLeft) {
                parent.scrollLeft = el.offsetLeft - parent.offsetLeft;
            }

            if (el === parentWrapper.firstElementChild) {
                parent.scrollLeft = 0;
            } else if (el === parentWrapper.lastElementChild) {
                parent.scrollLeft = 10000;
            }

        }

    };

    useEffect(() => {
        let scrollTimeout: string | number | NodeJS.Timeout | undefined;
        window.addEventListener('scroll', function(e) {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(function() {
                scrollToActive();
            }, 100);
        });
        window.scroll(0, 1);
    }, []);

    const handleLinkClick = (uid: string) => {

        setScrolling(true);

        document.querySelector(`#${uid}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

        setTimeout(() => {
            setScrolling(false);

        }, 1000);
    };

    const links = () => categories?.filter((cat) => cat.name.toLowerCase() !== 'upsell').map((category) => {
        if (category.is_hidden) {
            return null;
        }

        return (
            <NavPill
                size={'md'}
                key={`${category.uid}-nav`}
                id={`${category.uid}-nav`}
                variant={themeContext.v4.navigation_style}
            >
                <button onClick={() => handleLinkClick(category.uid)} >
                    <h3>
                        <span>{category.name}</span>
                    </h3>
                </button>
            </NavPill>
        );
    });

    if (!categories) {
        return null;
    }

    return (
        <>
            <Wrapper>
                <StyledNav
                    className={`d-block${navHorizontalScroll > 0 ? ' scrolled' : ''}`}
                >
                    <ScrollspyWrapper className="scrollSpyScroll" ref={ref}>
                        <Scrollspy
                            offset={bannerOffset && bannerOffsetHeight ? -80 - bannerOffsetHeight : -80}
                            componentTag={'div'}
                            items={categories?.filter((cat) => cat.name.toLowerCase() !== 'upsell').map((category) => `${category.uid}`)}
                            currentClassName="active"
                            className={`myScroll`}
                            rootEl={'.scrollSpyScroll'}
                            onUpdate={ () => { scrollToActive() } }
                        >
                            {links()}
                        </Scrollspy>
                    </ScrollspyWrapper>
                </StyledNav>
                <Shadow/>
            </Wrapper>
        </>
    );
};



const Shadow = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    bottom:0;
    width: 32px;
    background: linear-gradient(90deg, var(--layouts_nav_bg_opa0), var(--layouts_nav_bg));
    z-index: 2;
`;

// The nav scrolls sideways, which clips anything outside it, so a pill styled to move left on hover would be cut
// off at the first. On a wide screen it starts further left, over the page's own padding, with that padding
// put back inside it: the first pill is where it was, with room to move.
const NAV_BLEED = '24px';

const Wrapper = styled.div`
    position: relative;
    flex-grow: 1;
    overflow: hidden;

    @media screen and (min-width: 768px) {
        margin-left: -${NAV_BLEED};
    }
`;

const NavNext = styled.a`
    right: 0;
    top: 0;
    padding-right: 8px;
    margin-right: 20px;
    box-shadow: -4px 0px 8px 8px white;
    i { transition: scale 0.4s ease-in-out, box-shadow 0.2s ease-in-out; }
    &:hover {
        i {
            transform: scale(1.05);
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
    }

    /* new idea.. */
    box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.12);
    transition: box-shadow .2s ease-in-out, background-color 0.2s ease-in-out;
    padding-right: 0;
    border-radius: 50%;
    width: 30px;
`;

const NavPrev = styled.a`
    left: 0;
    margin-left: 20px;
    box-shadow: 4px 0px 8px 8px white;
    padding-left: 8px;
    display: none;
    i { transition: scale 0.4s ease-in-out, box-shadow 0.2s ease-in-out; }
    &:hover {
        i {
            transform: scale(1.05);
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
    }
`;

const StyledNav = styled(Nav)`
    max-width: 100%;
    margin: 0 auto;
    height: auto;
    overflow: hidden;
    overflow-x: auto;
    white-space: nowrap;
    z-index: 2;
    position: relative;

    .nav-arrow {
        width: 38px;
        height: 30px;
        position: absolute;
        color: var(--ui_nav_channels_pill_label);
        background-color: white;
        top: 50%;
        transform: translateY(-50%);

        /* new idea.. */
        box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.12);
        transition: box-shadow .2s ease-in-out, background-color 0.2s ease-in-out;
        padding: 0;
        border-radius: 50%;
        width: 30px;
        display: none;

        & i {
            font-size: 24px;
            line-height: 30px;
            width: 30px;
            text-align: center;
            background-color: white;
            border-radius: 50%;
        }
    }

    @media screen and (min-width: 768px) {
        .nav-arrow {
            display: block;
        }
        &.scrolled .nav-arrow-prev {
            display: block;
        }
    }
`;

const ScrollspyWrapper = styled.div`
    display: flex;
    flex-direction: row;
    overflow-x: scroll;
    scrollbar-width: none;
    scroll-behavior: smooth;

    @media screen and (max-width: 767px) { padding: 0 16px; }
    @media screen and (min-width: 768px) { padding-left: ${NAV_BLEED}; }

    > div {
        display: flex;
    }
`;

export default MenuCategorySelector;
